'use client';
import React, { useState } from 'react';
import Link from 'next/link';

interface ServicePageLayoutProps {
  icon: string;
  badge: string;
  title: string;
  subtitle: string;
  description: string;
  price: string;
  priceNote: string;
  whatsappText: string;
  bookingLink: string;
  features: string[];
  includes: string[];
  benefits: string[];
  ctaLabel: string;
  disclaimer?: string;
}

export default function ServicePageLayout({
  icon,
  badge,
  title,
  subtitle,
  description,
  price,
  priceNote,
  whatsappText,
  bookingLink,
  features,
  includes,
  benefits,
  ctaLabel,
  disclaimer,
}: ServicePageLayoutProps) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Hello Shruti, I am ${formData.name} and I am interested in ${whatsappText}. ${formData.message}. Email: ${formData.email}. Phone: ${formData.phone}`;
    window.open(`https://wa.me/917049753125?text=${encodeURIComponent(msg)}`, '_blank');
    setSubmitted(true);
  };

  return (
    <>
      {/* Hero */}
      <section
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24 pb-16"
        style={{ background: 'linear-gradient(160deg, #0d0818 0%, #130a24 40%, #1a0d30 70%, #0d0818 100%)' }}
      >
        <div className="absolute inset-0 lotus-bg opacity-40 pointer-events-none" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 glow-orb-lavender" style={{ width: '400px', height: '400px', opacity: 0.5 }} />
          <div className="absolute bottom-1/4 right-1/4 glow-orb-gold" style={{ width: '350px', height: '350px', opacity: 0.4 }} />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 mb-6 text-xs text-[#c4b5fd]/50">
            <Link href="/" className="hover:text-[#c9a84c] transition-colors">Home</Link>
            <span>›</span>
            <Link href="/#services" className="hover:text-[#c9a84c] transition-colors">Services</Link>
            <span>›</span>
            <span className="text-[#c9a84c]">{badge}</span>
          </div>

          <div className="text-5xl mb-4">{icon}</div>
          <div className="inline-block px-4 py-1.5 rounded-full text-xs tracking-widest uppercase mb-4" style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c' }}>
            {badge}
          </div>
          <h1
            className="font-serif text-4xl md:text-6xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, #b8a0e8 0%, #c9a84c 40%, #e8c96a 65%, #c9a84c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {title}
          </h1>
          <p
            className="font-serif text-xl italic mb-6"
            style={{
              background: 'linear-gradient(135deg, #9b7fe8, #b8a0e8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {subtitle}
          </p>

          {/* Price + CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <div className="flex items-baseline gap-1">
              <span
                className="font-display text-4xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #c9a84c, #e8c96a)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {price}
              </span>
              <span className="text-[#c4b5fd]/60 text-sm">{priceNote}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={bookingLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
              {ctaLabel}
            </a>
            <a
              href={`https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20${encodeURIComponent(whatsappText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-xs tracking-widest uppercase transition-all duration-300 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #25D366 0%, #1ebe5d 100%)', color: '#fff', boxShadow: '0 4px 20px rgba(37,211,102,0.3)' }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp Inquiry
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <section
        className="relative py-20 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0d0818 0%, #130a24 50%, #0d0818 100%)' }}
      >
        <div className="absolute inset-0 lotus-bg opacity-25 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div
                className="rounded-3xl p-8"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(201,168,76,0.22)' }}
              >
                <h2 className="font-serif text-2xl font-semibold text-[#e8dff5] mb-4">About This Program</h2>
                <p className="text-[#c4b5fd]/75 text-sm leading-relaxed">{description}</p>
                {disclaimer && (
                  <div className="mt-4 p-3 rounded-xl text-xs text-[#c4b5fd]/50 italic" style={{ background: 'rgba(155,127,232,0.08)', border: '1px solid rgba(155,127,232,0.15)' }}>
                    ⚠️ {disclaimer}
                  </div>
                )}
              </div>

              {/* What We Address */}
              <div
                className="rounded-3xl p-8"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(155,127,232,0.22)' }}
              >
                <h2 className="font-serif text-2xl font-semibold text-[#e8dff5] mb-6">What We Address</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.map((feature) => (
                    <div
                      key={feature}
                      className="flex items-center gap-3 p-3 rounded-xl text-sm text-[#e8dff5]/80"
                      style={{ background: 'rgba(155,127,232,0.08)', border: '1px solid rgba(155,127,232,0.15)' }}
                    >
                      <span className="text-[#c9a84c] flex-shrink-0">✦</span>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div
                className="rounded-3xl p-8"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(201,168,76,0.22)' }}
              >
                <h2 className="font-serif text-2xl font-semibold text-[#e8dff5] mb-6">Key Benefits</h2>
                <div className="space-y-3">
                  {benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm text-[#c4b5fd]/80">
                      <span className="text-[#c9a84c] mt-0.5 flex-shrink-0">✓</span>
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>

              {/* Inquiry Form */}
              <div
                className="rounded-3xl p-8"
                style={{ background: 'rgba(22, 12, 40, 0.85)', border: '1px solid rgba(201,168,76,0.25)' }}
              >
                <h2 className="font-serif text-2xl font-semibold text-[#e8dff5] mb-6">Send an Inquiry</h2>
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">✨</div>
                    <p className="text-[#c9a84c] font-serif text-lg mb-2">Thank You!</p>
                    <p className="text-[#c4b5fd]/70 text-sm">Your inquiry has been sent via WhatsApp. Shruti will get back to you shortly.</p>
                    <button onClick={() => setSubmitted(false)} className="mt-4 btn-outline-gold text-xs">Send Another</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Name *</label>
                        <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your name" className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none" style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }} />
                      </div>
                      <div>
                        <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Phone</label>
                        <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none" style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Email *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="your@email.com" className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none" style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }} />
                    </div>
                    <div>
                      <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Message *</label>
                      <textarea required rows={3} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us about yourself and what you're seeking healing for..." className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none resize-none" style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }} />
                    </div>
                    <button type="submit" className="btn-gold w-full text-center">Send Inquiry via WhatsApp</button>
                  </form>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Booking Card */}
              <div
                className="rounded-3xl p-6 sticky top-24"
                style={{
                  background: 'linear-gradient(135deg, rgba(201,168,76,0.10) 0%, rgba(124,92,191,0.10) 100%)',
                  border: '1.5px solid rgba(201,168,76,0.35)',
                  boxShadow: '0 8px 40px rgba(201,168,76,0.10)',
                }}
              >
                <div className="text-center mb-6">
                  <div className="text-[#c9a84c]/60 text-xs tracking-widest uppercase mb-1">Investment</div>
                  <div
                    className="font-display text-4xl font-bold"
                    style={{
                      background: 'linear-gradient(135deg, #c9a84c, #e8c96a)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {price}
                  </div>
                  <div className="text-[#c4b5fd]/50 text-sm">{priceNote}</div>
                </div>

                <div className="space-y-3 mb-6">
                  {includes.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-xs text-[#c4b5fd]/75">
                      <span className="text-[#c9a84c] mt-0.5 flex-shrink-0">✓</span>
                      {item}
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <a href={bookingLink} target="_blank" rel="noopener noreferrer" className="btn-gold w-full text-center block">
                    {ctaLabel}
                  </a>
                  <a
                    href={`https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20book%20${encodeURIComponent(whatsappText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 hover:scale-105"
                    style={{ background: 'rgba(37,211,102,0.10)', border: '1.5px solid rgba(37,211,102,0.4)', color: '#25D366' }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp Inquiry
                  </a>
                </div>

                {/* Payment Options */}
                <div className="mt-5 pt-5" style={{ borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                  <div className="text-[#c9a84c]/60 text-xs tracking-widest uppercase mb-3">Payment Options</div>
                  <div className="flex flex-wrap gap-2">
                    {['UPI', 'Razorpay', 'PhonePe', 'GPay', 'Cards', 'Net Banking'].map((method) => (
                      <span key={method} className="text-[10px] px-2 py-1 rounded-full text-[#c4b5fd]/60" style={{ background: 'rgba(155,127,232,0.10)', border: '1px solid rgba(155,127,232,0.18)' }}>
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
